<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { IBulkEmailsSchema } from '@modules/bulk-emails/types';
import type {
  FormFlattenKey,
  FormFlattenValue,

  FormGroup,
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import BulkEmailFilters from '@modules/bulk-emails/components/BulkEmailFilters/BulkEmailFilters.vue';
import TextEditorPreviewIframe from '@modules/bulk-emails/components/TextEditorPreviewIframe/TextEditorPreviewIframe.vue';
import { TRANSLATION_GROUP_BULK_EMAILS } from '@modules/bulk-emails/constants';
import { useBulkEmailSendMutation } from '@modules/bulk-emails/mutations';
import { useBulkEmailsOptionsQuery } from '@modules/bulk-emails/queries';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useToastStore } from '@modules/toast/store';
import {
  assert,
  isOptionalString,
} from '@utils/guards';

const { data: options, isLoading } = useBulkEmailsOptionsQuery();
const {
  mutateAsync: sendEmail,
  isLoading: isLoadingSave,
  errors,
  clearError,
} = useBulkEmailSendMutation();

const { displayPresetToast } = useToastStore();
const { getTranslation } = useI18n();

const isEditorMode = ref<boolean>();
const editorPreview = ref<string>();
const isTestDisabled = ref(true);

const formSchema = computed<FormSchema<IBulkEmailsSchema, 'filters'>>(() => {
  const schema: FormSchema<IBulkEmailsSchema, 'filters'> = {
    filters: {
      type: EFormInputTypes.SLOT,
    },
    subject: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'EMAIL_SUBJECT').value,
      required: true,
    },
    sender_email: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'SENDER_EMAIL').value,
      required: true,
      value: options.value?.sender_email,
    },
    sender_name: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'SENDER_NAME').value,
      required: true,
      value: options.value?.sender_name,
    },
    isEditorMode: {
      type: EFormInputTypes.SWITCH,
      label: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'MODE_SWITCHER_LABEL').value,
      falseLabel: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'MODE_EDITOR').value,
      trueLabel: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'MODE_HTML').value,
      value: isEditorMode.value,
    },
    emails: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'EMAILS_FOR_TESTING').value,
      description: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'EMAIL_TEST_DESCRIPTION').value,
    },
  };

  if (isEditorMode.value) {
    schema.editorBody = {
      type: EFormInputTypes.TEXTAREA,
      label: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'MODEL_HTML_TEXTAREA').value,
      isFullWidth: true,
    };

    schema.editorPreview = {
      type: EFormInputTypes.SLOT,
    };
  } else {
    schema.htmlBody = {
      type: EFormInputTypes.TEXT_EDITOR,
      variables: options.value?.consts_and_variables,
    };
  }

  return schema;
});

const formGroups = computed<FormGroup<IBulkEmailsSchema, 'filters'>[]>(() => ([
  {
    items: [
      'filters',
      'subject',
      'sender_email',
      'sender_name',
    ],
  },
  {
    title: getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'EMAIL_BODY').value,
    items: [
      'isEditorMode',
      'htmlBody',
      'editorBody',
      'editorPreview',
    ],
  },
  {
    items: [
      'emails',
    ],
  },
]));

function onFormChange<
  T extends IBulkEmailsSchema,
  K extends FormFlattenKey<T, 'filters'>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  if (key === 'isEditorMode') {
    isEditorMode.value = !!value;
  }

  if (key === 'editorBody') {
    assert(isOptionalString(value));
    editorPreview.value = value;
  }

  if (key === 'emails') {
    isTestDisabled.value = !value;
  }

  clearError(key);
}

let isTest = false;
const form = ref<VFormRef>();

function onClickTest(): void {
  isTest = true;
  form.value?.submit();
}

async function onFormSubmit(values: IBulkEmailsSchema): Promise<void> {
  try {
    await sendEmail({
      emails: values.emails,
      filters: values.filters,
      letter: {
        subject: values.subject,
        sender_email: values.sender_email,
        sender_name: values.sender_name,
        body: values.isEditorMode ? values.editorBody : values.htmlBody,
      },
      isTest,
    });

    displayPresetToast('success', getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'EMAIL_WAS_SENT').value);
  } finally {
    isTest = false;
  }
}
</script>

<template>
  <VCard v-data-qa-auto-id="'BulkEmailsPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'HEADER')"
  >
    <VLoader
      :loading="isLoading"
      min-height="200px"
    >
      <VForm
        ref="form"
        :schema="formSchema"
        :groups="formGroups"
        :custom-errors="errors"
        :is-loading="isLoadingSave"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'SEND_BTN')"
        @interacted="onFormChange"
        @submit="onFormSubmit"
      >
        <template #filters="{ className, onChange, errors: filterErrors }">
          <BulkEmailFilters
            :class="className"
            :errors="filterErrors"
            @change="onChange('filters', $event)"
          />
        </template>
        <template #editorPreview>
          <div>
            <TextEditorPreviewIframe
              v-if="editorPreview"
              :html="editorPreview"
              :label="$getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'MODE_PREVIEW_EMAIL')"
            />
          </div>
        </template>
        <template #secondaryBtn>
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_BULK_EMAILS, 'SEND_TEST_BTN')"
            :disabled="isTestDisabled"
            :loading="isLoadingSave"
            @click="onClickTest"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
